// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useEffect } from 'react';
import { AppIcon } from '@loa/ui-components';

type RelationshipActionMenuProps = Readonly<{
  onEdit: () => void;
  onChangeOfAgency?: () => void;
  onProjectionAssumptions?: () => void;
  className?: string;
}>;

export function RelationshipActionMenu({
  onEdit,
  onChangeOfAgency,
  onProjectionAssumptions,
  className = '',
}: RelationshipActionMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="cursor-pointer flex items-center justify-center w-[40px] h-[40px] rounded-lg border border-purple-primary bg-transparent hover:bg-lilac-06 transition-colors"
        aria-label="Relationship actions"
        data-testid="relationship-actions-trigger"
      >
        <AppIcon name="more" size={24} color="#2D0A6C" />
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+8px)] right-0 w-[280px] inline-flex p-md flex-col items-start gap-sm rounded-lg bg-white z-50"
          style={{ boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.10)' }}
        >
          <button
            type="button"
            className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left w-full"
            onClick={() => { setOpen(false); onEdit(); }}
            data-testid="relationship-actions-edit"
          >
            <AppIcon name="edit" size={16} color="#141414" />
            <span className="text-black font-sans text-body-02 font-normal leading-normal">Edit relationship</span>
          </button>

          {onChangeOfAgency && (
            <button
              type="button"
              className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left w-full"
              onClick={() => { setOpen(false); onChangeOfAgency(); }}
              data-testid="relationship-actions-coa"
            >
              <AppIcon name="change of agency" size={16} color="#141414" />
              <span className="text-black font-sans text-body-02 font-normal leading-normal">Change of agency</span>
            </button>
          )}

          {onProjectionAssumptions && (
            <button
              type="button"
              className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left w-full"
              onClick={() => { setOpen(false); onProjectionAssumptions(); }}
              data-testid="relationship-actions-assumptions"
            >
              <AppIcon name="settings gear" size={16} color="#141414" />
              <span className="text-black font-sans text-body-02 font-normal leading-normal whitespace-nowrap">Projection assumptions</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
